import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, IdCard, Check, X } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { usePageMeta } from "@/lib/seo";

const FRAMES = [
  ["01", "The headshot", "Face forward, shoulders relaxed, eyes into the lens. Shot from the chest up in soft, natural daylight — this is the frame every agent looks at first, and the one your comp card leads with.", "https://images.unsplash.com/photo-1674932668403-33398b81c92f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwbW9kZWwlMjBiZWF1dHklMjBoZWFkc2hvdCUyMG1pbmltYWwlMjBtYWtldXAlMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTM1MDI2Njl8MA&ixlib=rb-4.1.0&q=85", "Model digital headshot portrait with clean face-forward framing and minimal makeup"],
  ["02", "The profile", "A clean side view, left or right. Houses need to read your bone structure and profile line — no hair covering the face, chin level, neutral expression.", "https://images.unsplash.com/photo-1614942925739-a21796eba758?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwbW9kZWwlMjBzaWRlJTIwcHJvZmlsZSUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc5MzUwMjY2OXww&ixlib=rb-4.1.0&q=85", "Side-profile studio portrait of model with hair pulled back and neutral expression"],
  ["03", "The full length", "Head to toe, standing straight, fitted neutral clothing. This is the frame that answers height and proportions before anyone reads your measurements.", "https://images.unsplash.com/photo-1701844778533-f46d8092ebe1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwbW9kZWwlMjBmdWxsJTIwbGVuZ3RoJTIwc3R1ZGlvJTIwcG9ydHJhaXQlMjBzdGFuZGluZ3xlbnwwfHx8fDE3OTM1MDI2Njl8MA&ixlib=rb-4.1.0&q=85", "Full-length standing portrait of model in neutral fitted studio wear"],
  ["04", "The three-quarter", "Body turned slightly, weight on one hip, a hint of movement. Shows how you carry yourself — the difference between a photo of a person and a photo of a model.", "https://images.unsplash.com/photo-1715541448446-3369e1cc0ee9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwbW9kZWwlMjB0aHJlZSUyMHF1YXJ0ZXIlMjBwb3J0cmFpdCUyMHN0dWRpbyUyMHBvc2V8ZW58MHx8fHwxNzkzNTAyNjc2fDA&ixlib=rb-4.1.0&q=85", "Three-quarter studio fashion portrait of model with poised body angle"],
  ["05", "The editorial", "One frame with personality — a look, a mood, a styled shot you love. This is where range lives. Keep it honest: you, on a very good day, not someone else.", "https://images.unsplash.com/photo-1788500657759-6af4660f20eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxoaWdoJTIwZmFzaGlvbiUyMGVkaXRvcmlhbCUyMHBvcnRyYWl0JTIwZHJhbWF0aWMlMjBsaWdodGluZyUyMHN0dWRpb3xlbnwwfHx8fDE3OTM1MDI2NzZ8MA&ixlib=rb-4.1.0&q=85", "Styled high-fashion editorial photo frame with moody dramatic lighting"],
];

const RULES = [
  "Shoot in soft daylight — window light or open shade beats any filter",
  "Plain, uncluttered background — a wall, not a bedroom",
  "Fitted, neutral clothing — houses need to see your lines, not your wardrobe",
  "Little to no makeup for your digitals, hair off the face",
  "Recent frames only — nothing older than twelve months",
  "High resolution, sharp focus, honest color",
];

const NOT_THIS = [
  "Mirror selfies, car selfies, bathroom lighting",
  "Beauty-app smoothing or heavy filters — agents spot them instantly",
  "Sunglasses, hats or hair hiding your face",
  "Group photos, party shots, ex-partners cropped out",
  "Watermarks, logos, collages or text overlays",
  "Heavy retouching — if you walk in looking different, the booking ends there",
];

const BEYOND_PHOTOS = [
  ["06", "Measurements", [
    "Houses cast by numbers before they ever read a name — your measurements decide whether you get opened. On Solis you enter them in US units: height in feet and inches, bust, waist and hips in inches, plus shoe size, hair and eye color.",
    "Measure honestly, barefoot, against a wall: bust at the fullest point, waist at its natural narrowest, hips at the widest. Round down your height and you won't get the call; round up and you'll lose the booking in the fitting. Agents compare your numbers against your full-length frame — they have to match.",
    "Re-measure every few months and keep them current. An out-of-date book wastes everyone's call time — starting with yours.",
  ]],
  ["07", "The video reel", [
    "One short professional video lifts a book above the pile. Thirty to sixty seconds is plenty: soft daylight, plain backdrop, fitted neutral clothing — the same discipline as your digitals.",
    "Walk toward the camera and away, turn slowly both sides, then look into the lens and say your name, height and city. Hold the camera steady or prop it — shaky phone footage undersells you.",
    "Upload it as an MP4 from your dashboard under Reel. No music, no effects, no montage — a walking video is what agents are trained to read.",
  ]],
  ["08", "Credits", [
    "Credits are your track record: each one names the brand or publication, what the work was — campaign, editorial, runway, lookbook, e-commerce — and the year. Three real credits outrank ten vague ones.",
    "New to this? Test shoots count. 'Test editorial — photographer's name, 2026' is an honest starter credit and every working model began with one.",
    "Never invent a credit. This industry is small, houses know each other, and a single false credit ends trust permanently.",
  ]],
  ["09", "The description", [
    "Two to four sentences, plain words. Who you are, where you're based, your lane — editorial, runway, commercial, fitness — and one thing that makes you memorable. That is the entire assignment.",
    "Skip the clichés: every agent alive has read 'passionate about fashion since childhood' ten thousand times. Write what you can do — 'walks in heels for eight hours', 'swims competitively', 'speaks French and Yoruba' — because those are the lines that get searched for and remembered.",
    "No emoji, no hashtags, no life story. The photographs show the look; the description tells the house what it's like to book you.",
  ]],
];

export default function PortfolioGuide() {
  usePageMeta({
    title: "The Portfolio Standard — How to Build a Model Book | Solis Casting",
    description: "What a professional model portfolio should include — the five frames every house expects, honest measurements, a video reel, credits, a bio that stands out, and how your free digital comp card works.",
    url: `${window.location.origin}/portfolio-guide`,
  });
  return (
    <div className="px-6 sm:px-10 py-24" data-testid="portfolio-guide-page">
      <Reveal>
        <p className="overline-label mb-4">For models</p>
        <h1 className="font-display font-light text-4xl sm:text-6xl tracking-tighter mb-6">
          The Portfolio <span className="italic text-accent">Standard</span>
        </h1>
        <p className="text-sm text-white/60 max-w-2xl mb-24 leading-relaxed">
          Houses decide in seconds. Your book doesn't need a big-budget shoot — it needs the right five frames,
          honest measurements, one short reel, real credits and a description worth reading. This is exactly what
          casting directors expect to see, in the order they expect to see it.
        </p>
      </Reveal>

      <section className="mb-32" data-testid="guide-frames">
        <Reveal>
          <p className="overline-label mb-4 flex items-center gap-2"><Camera size={14} className="text-accent" /> The five frames</p>
          <h2 className="font-display font-light text-3xl sm:text-5xl tracking-tight mb-16">
            Every book needs <span className="italic text-accent">these five</span>
          </h2>
        </Reveal>
        <div className="border-t border-white/10">
          {FRAMES.map(([n, title, body, img, alt], i) => (
            <Reveal key={n} delay={i * 0.06}>
              <div className="grid grid-cols-12 gap-6 sm:gap-10 py-12 border-b border-white/10 items-center" data-testid={`guide-frame-${i}`}>
                <div className={`col-span-12 sm:col-span-4 lg:col-span-3 ${i % 2 ? "sm:order-2" : ""}`}>
                  <div className="group overflow-hidden aspect-[3/4] border border-white/10 hover:border-accent/50 transition-colors duration-500" data-testid={`guide-frame-photo-${i}`}>
                    <img
                      src={img}
                      alt={alt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-white/35 mt-3">Digital {n} — {title}</p>
                </div>
                <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                  <span className="font-display italic text-2xl text-accent block mb-2">{n}</span>
                  <span className="font-display text-3xl sm:text-4xl block mb-4">{title}</span>
                  <span className="text-sm text-white/70 leading-relaxed max-w-xl block">{body}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mb-32 grid grid-cols-1 lg:grid-cols-2 gap-16" data-testid="guide-rules">
        <Reveal>
          <div>
            <p className="overline-label mb-8">Use this</p>
            <div className="flex flex-col gap-5">
              {RULES.map((r, i) => (
                <div key={i} className="flex items-start gap-4" data-testid={`guide-rule-${i}`}>
                  <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-white/80 leading-relaxed">{r}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div>
            <p className="overline-label mb-8">Not this</p>
            <div className="flex flex-col gap-5">
              {NOT_THIS.map((r, i) => (
                <div key={i} className="flex items-start gap-4" data-testid={`guide-not-${i}`}>
                  <X size={16} className="text-accent shrink-0 mt-0.5" />
                  <p className="text-sm text-white/60 leading-relaxed">{r}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mb-32" data-testid="guide-beyond-photos">
        <Reveal>
          <p className="overline-label mb-4">Beyond the photographs</p>
          <h2 className="font-display font-light text-3xl sm:text-5xl tracking-tight mb-16">
            The rest of <span className="italic text-accent">the book</span>
          </h2>
        </Reveal>
        <div className="border-t border-white/10">
          {BEYOND_PHOTOS.map(([n, title, paragraphs], i) => (
            <Reveal key={n} delay={i * 0.06}>
              <div className="grid grid-cols-12 gap-6 sm:gap-10 py-12 border-b border-white/10" data-testid={`guide-extra-${i}`}>
                <div className="col-span-12 sm:col-span-4">
                  <span className="font-display italic text-2xl text-accent block mb-2">{n}</span>
                  <span className="font-display text-3xl sm:text-4xl block">{title}</span>
                </div>
                <div className="col-span-12 sm:col-span-8 space-y-4 max-w-2xl">
                  {paragraphs.map((p, j) => (
                    <p key={j} className="text-sm text-white/70 leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-16 max-w-3xl border-l-2 border-accent pl-8" data-testid="guide-bio-example">
            <p className="overline-label mb-4 !text-[10px]">A description that works</p>
            <p className="font-display italic text-2xl sm:text-3xl leading-snug text-white/90">
              "Editorial and runway model based in New York, 5'11", with four seasons of show experience
              and e-commerce work for three national retailers. Trained dancer — comfortable with movement
              direction and long call times. Available for travel."
            </p>
            <p className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-white/40 mt-4">
              Thirty-two words. Lane, stats, proof, availability — nothing else needed.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="mb-32" data-testid="guide-comp-card">
        <Reveal>
          <div className="panel p-10 sm:p-16 relative overflow-hidden">
            <p className="overline-label mb-4 flex items-center gap-2"><IdCard size={14} className="text-accent" /> Included with every profile</p>
            <h2 className="font-display font-light text-3xl sm:text-5xl tracking-tight mb-8 max-w-2xl">
              Your comp card builds <span className="italic text-accent">itself</span>
            </h2>
            <div className="max-w-3xl space-y-5 text-sm text-white/75 leading-relaxed">
              <p>
                Every Solis profile comes with a digital comp card — the one-page summary casting agents actually
                work from — and you never have to design anything. It assembles automatically from
                <span className="text-white"> your first five pictures</span>: your cover photo up front, the next
                four frames beside it, with your measurements, details and selected credits laid in.
              </p>
              <p>
                Casting agents can open your comp card straight from your portfolio page and
                <span className="text-white"> download or print it</span> to bring into their casting session.
                No file uploads, no PDF to maintain — update your photos and the card updates with them.
              </p>
              <p className="text-white/90">
                Which is why the order of your book matters: your first five photos are not just your gallery —
                they are your comp card. Lead with the headshot, follow with the frames above, and every agent
                who opens your card sees you at your strongest.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <Reveal>
        <div className="flex flex-wrap items-center gap-6" data-testid="guide-cta">
          <Link to="/auth?mode=register&role=model" data-testid="guide-apply-cta" className="btn-primary inline-flex items-center gap-2">
            Build your book <ArrowUpRight size={16} />
          </Link>
          <Link to="/models" className="btn-outline">See the standard in action</Link>
        </div>
      </Reveal>
    </div>
  );
}
